// MobileAccessManager.js
import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Button, Input, message, Modal, Space, Switch, Table, Tag, Tooltip, Typography} from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    CopyOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    KeyOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserOutlined
} from '@ant-design/icons';
import {instance} from '../../API/API';

const {Text, Paragraph} = Typography;

// Компонент статуса доступа
const AccessStatus = ({access, lastLogin}) => {
    const color = access ? 'green' : 'red';
    const text = access ? 'Доступ открыт' : 'Доступ закрыт';

    return (
        <Space>
            <Tag color={color}>
                {access ? <CheckCircleOutlined/> : <CloseCircleOutlined/>}
                {text}
            </Tag>
            {lastLogin && lastLogin > 0 && (
                <Tooltip title={`Последний вход: ${new Date(lastLogin * 1000).toLocaleString()}`}>
                    <Tag color="blue">Был в системе</Tag>
                </Tooltip>
            )}
        </Space>
    );
};

// Модальное окно управления доступом
const AccessModal = ({
                         visible,
                         staff,
                         onCancel,
                         onGenerate,
                         onResetPassword,
                         loading
                     }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [generatedData, setGeneratedData] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);

    // Сбрасываем состояние при открытии/закрытии
    useEffect(() => {
        if (!visible) {
            setGeneratedData(null);
            setShowPassword(false);
        }
    }, [visible]);

    if (!staff) return null;

    const fullName = `${staff.lastname || ''} ${staff.firstname || ''} ${staff.patronymic || ''}`.trim();

    const handleGenerate = async () => {
        const result = await onGenerate(staff.id);
        if (result && result.success) {
            setGeneratedData(result.data);
            setShowPassword(true);
            message.success('Логин и пароль успешно сгенерированы!');
        }
    };

    const handleResetPassword = async () => {
        const result = await onResetPassword(staff.id);
        if (result && result.success) {
            setGeneratedData(result.data);
            setShowPassword(true);
            message.success('Пароль успешно сброшен!');
        }
    };

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
            message.success('Скопировано в буфер обмена!');
        } catch (err) {
            message.error('Не удалось скопировать');
        }
    };

    const hasAccess = staff.mobile_access === 1;
    const hasLogin = staff.mobile_login && staff.mobile_login !== '';

    return (
        <Modal
            title={
                <Space>
                    <UserOutlined/>
                    <span>Управление доступом</span>
                    <Tag color="blue">{fullName}</Tag>
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Закрыть
                </Button>,
                hasAccess && hasLogin && (
                    <Button
                        key="reset"
                        onClick={handleResetPassword}
                        icon={<ReloadOutlined/>}
                        loading={loading}
                    >
                        Сбросить пароль
                    </Button>
                ),
                !hasLogin && (
                    <Button
                        key="generate"
                        type="primary"
                        onClick={handleGenerate}
                        icon={<KeyOutlined/>}
                        loading={loading}
                    >
                        Сгенерировать доступ
                    </Button>
                )
            ]}
            width={600}
        >
            <div style={{padding: '10px 0'}}>
                <Alert
                    message="Информация"
                    description={
                        hasLogin
                            ? `Логин уже создан: ${staff.mobile_login}`
                            : 'Сотруднику еще не сгенерирован логин и пароль'
                    }
                    type={hasLogin ? 'info' : 'warning'}
                    showIcon
                    style={{marginBottom: 16}}
                />

                {generatedData && (
                    <div style={{
                        background: '#f0f5ff',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #d6e4ff',
                        marginBottom: 16
                    }}>
                        <div style={{marginBottom: 12}}>
                            <Text strong>Логин: </Text>
                            <Text code style={{fontSize: '14px'}}>{generatedData.login}</Text>
                            <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined/>}
                                onClick={() => handleCopy(generatedData.login)}
                                style={{marginLeft: 8}}
                            />
                        </div>

                        <div>
                            <Text strong>Пароль: </Text>
                            <Space>
                                {showPassword ? (
                                    <Text code style={{fontSize: '16px', letterSpacing: '1px'}}>
                                        {generatedData.password}
                                    </Text>
                                ) : (
                                    <Text code style={{fontSize: '16px', letterSpacing: '2px'}}>
                                        ••••••••••
                                    </Text>
                                )}
                                <Button
                                    type="text"
                                    size="small"
                                    icon={showPassword ? <EyeInvisibleOutlined/> : <EyeOutlined/>}
                                    onClick={() => setShowPassword(!showPassword)}
                                />
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined/>}
                                    onClick={() => handleCopy(generatedData.password)}
                                />
                            </Space>
                        </div>

                        <div style={{marginTop: 12}}>
                            <Alert
                                message="Важно!"
                                description="Пароль отображается только один раз. После закрытия модального окна его нельзя будет увидеть. При необходимости используйте кнопку 'Сбросить пароль'."
                                type="warning"
                                showIcon
                            />
                        </div>
                    </div>
                )}

                {hasAccess && hasLogin && !generatedData && (
                    <div style={{marginTop: 16}}>
                        <Alert
                            message="Доступ уже настроен"
                            description={
                                <Space direction="vertical">
                                    <div>Логин: <Text code>{staff.mobile_login}</Text></div>
                                    <div>Для просмотра нового пароля нажмите "Сбросить пароль".</div>
                                </Space>
                            }
                            type="success"
                            showIcon
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

// Основной компонент
const MobileAccessManager = () => {
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Загрузка списка сотрудников
    const fetchStaff = async () => {
        setLoading(true);
        try {
            const response = await instance.get('staff/get_mobile_staff.php');
            if (response.data && response.data.success) {
                setStaffList(response.data.data || []);
            } else {
                message.error(response.data?.error || 'Ошибка загрузки данных');
            }
        } catch (error) {
            console.error('Error fetching staff:', error);
            message.error('Ошибка загрузки списка сотрудников');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    // Обновление доступа
    const handleAccessToggle = async (staffId, checked) => {
        try {
            const response = await instance.post('staff/update_mobile_access.php', {
                staff_id: staffId,
                mobile_access: checked ? 1 : 0
            });

            if (response.data && response.data.success) {
                setStaffList(prev =>
                    prev.map(item =>
                        item.id === staffId
                            ? {...item, mobile_access: checked ? 1 : 0}
                            : item
                    )
                );
                message.success(`Доступ ${checked ? 'открыт' : 'закрыт'}`);
            } else {
                message.error(response.data?.error || 'Ошибка обновления доступа');
            }
        } catch (error) {
            console.error('Error updating access:', error);
            message.error('Ошибка обновления доступа');
        }
    };

    // Генерация логина и пароля
    const handleGenerate = async (staffId) => {
        setModalLoading(true);
        try {
            const response = await instance.post('staff/generate_mobile_access.php', {
                staff_id: staffId
            });

            if (response.data && response.data.success) {
                setStaffList(prev =>
                    prev.map(item =>
                        item.id === staffId
                            ? {
                                ...item,
                                mobile_login: response.data.data.login,
                                mobile_access: 1
                            }
                            : item
                    )
                );
                return {success: true, data: response.data.data};
            } else {
                message.error(response.data?.error || 'Ошибка генерации');
                return {success: false};
            }
        } catch (error) {
            console.error('Error generating access:', error);
            message.error('Ошибка генерации доступа');
            return {success: false};
        } finally {
            setModalLoading(false);
        }
    };

    // Сброс пароля
    const handleResetPassword = async (staffId) => {
        setModalLoading(true);
        try {
            const response = await instance.post('staff/reset_mobile_password.php', {
                staff_id: staffId
            });

            if (response.data && response.data.success) {
                return {success: true, data: response.data.data};
            } else {
                message.error(response.data?.error || 'Ошибка сброса пароля');
                return {success: false};
            }
        } catch (error) {
            console.error('Error resetting password:', error);
            message.error('Ошибка сброса пароля');
            return {success: false};
        } finally {
            setModalLoading(false);
        }
    };

    const openModal = (staff) => {
        setSelectedStaff(staff);
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedStaff(null);
        setModalLoading(false);
    };

    // Фильтрация по поиску
    const filteredStaff = useMemo(() => {
        if (!searchText.trim()) return staffList;
        const search = searchText.toLowerCase();
        return staffList.filter(item => {
            const fullName = `${item.lastname || ''} ${item.firstname || ''} ${item.patronymic || ''}`.toLowerCase();
            return fullName.includes(search) ||
                (item.mobile_login && item.mobile_login.toLowerCase().includes(search));
        });
    }, [staffList, searchText]);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
            align: 'center',
        },
        {
            title: 'ФИО',
            key: 'fullName',
            render: (_, record) => {
                const fullName = `${record.lastname || ''} ${record.firstname || ''} ${record.patronymic || ''}`.trim();
                return <Text>{fullName || 'Не указано'}</Text>;
            },
            sorter: (a, b) => {
                const nameA = `${a.lastname || ''} ${a.firstname || ''}`;
                const nameB = `${b.lastname || ''} ${b.firstname || ''}`;
                return nameA.localeCompare(nameB);
            },
        },
        {
            title: 'Логин',
            dataIndex: 'mobile_login',
            key: 'mobile_login',
            render: (login) => (
                login ? (
                    <Tooltip title={login}>
                        <Text code>{login}</Text>
                    </Tooltip>
                ) : (
                    <Tag color="default">Не создан</Tag>
                )
            ),
        },
        {
            title: 'Доступ',
            dataIndex: 'mobile_access',
            key: 'mobile_access',
            align: 'center',
            render: (access, record) => (
                <Switch
                    checked={access === 1}
                    onChange={(checked) => handleAccessToggle(record.id, checked)}
                    checkedChildren="Вкл"
                    unCheckedChildren="Выкл"
                />
            ),
        },
        {
            title: 'Управление',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<KeyOutlined/>}
                    onClick={() => openModal(record)}
                    disabled={record.mobile_access !== 1}
                >
                    Настроить
                </Button>
            ),
        },
        {
            title: 'Статус',
            key: 'status',
            width: 200,
            render: (_, record) => (
                <AccessStatus
                    access={record.mobile_access}
                    lastLogin={record.mobile_last_login}
                />
            ),
        },
    ];

    return (
        <div style={{padding: '16px', background: '#fff', borderRadius: '8px'}}>
            <div style={{
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px'
            }}>
                <Space>
                    <h3 style={{margin: 0}}>Управление мобильным доступом</h3>
                    <Tag color="green">Всего: {filteredStaff.length}</Tag>
                </Space>
                <Space>
                    <Input
                        placeholder="Поиск по ФИО или логину..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{width: 250}}
                        allowClear
                        prefix={<SearchOutlined/>}
                    />
                    <Button onClick={fetchStaff} icon={<ReloadOutlined/>} loading={loading}>
                        Обновить
                    </Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={filteredStaff}
                loading={loading}
                rowKey="id"
                size="middle"
                pagination={false}
                locale={{emptyText: 'Нет сотрудников'}}
            />

            <AccessModal
                visible={modalVisible}
                staff={selectedStaff}
                onCancel={closeModal}
                onGenerate={handleGenerate}
                onResetPassword={handleResetPassword}
                loading={modalLoading}
            />
        </div>
    );
};

export default MobileAccessManager;